import { useState, useRef } from 'react';
import { Image as ImageIcon, Plus, Trash2, Calendar, MapPin, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useGlobalToast } from '@/contexts/ToastContext';

interface Props {
  data: any;
  onChange: (data: any) => void;
}

interface ProjectImage {
  url: string;
  file?: File;
}

export function PortfolioStep({ data, onChange }: Props) {
  const { error: showError } = useGlobalToast();
  const [showForm, setShowForm] = useState(false);
  const [currentProject, setCurrentProject] = useState({
    title: '',
    description: '',
    category: '',
    completedDate: '',
    location: '',
    images: [] as ProjectImage[]
  });
  const [dateError, setDateError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const portfolio = data.portfolio || [];

  // Get today's date in YYYY-MM-DD format for max date validation
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const handleDateChange = (date: string) => {
    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare dates only

    if (selectedDate > today) {
      setDateError('Completion date cannot be in the future');
      return;
    }

    setDateError('');
    setCurrentProject({ ...currentProject, completedDate: date });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (currentProject.images.length + files.length > 10) {
      showError('Too Many Images', 'You can upload maximum 10 images per project');
      return;
    }

    const newImages: ProjectImage[] = files.map(file => ({
      url: URL.createObjectURL(file),
      file
    }));

    setCurrentProject({
      ...currentProject,
      images: [...currentProject.images, ...newImages]
    });
  };

  const removeImage = (index: number) => {
    const newImages = currentProject.images.filter((_, i) => i !== index);
    setCurrentProject({ ...currentProject, images: newImages });
  };

  const addProject = () => {
    if (!currentProject.title.trim()) {
      showError('Missing Title', 'Please enter a project title');
      return;
    }
    if (!currentProject.description.trim()) {
      showError('Missing Description', 'Please enter a project description');
      return;
    }
    if (currentProject.images.length === 0) {
      showError('Missing Images', 'Please add at least one image');
      return;
    }
    if (dateError) {
      showError('Invalid Date', dateError);
      return;
    }

    onChange({
      ...data,
      portfolio: [...portfolio, { ...currentProject, id: Date.now().toString() }]
    });
    
    setCurrentProject({ 
      title: '', 
      description: '', 
      category: '', 
      completedDate: '', 
      location: '',
      images: []
    });
    setShowForm(false);
  };

  const removeProject = (id: string) => {
    onChange({
      ...data,
      portfolio: portfolio.filter((p: any) => p.id !== id)
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Portfolio <span className="text-sm font-normal text-gray-500">(Optional)</span>
        </h2>
        <p className="text-gray-600">Showcase your completed projects with photos and details</p>
      </div>

      {/* Portfolio Wall - Facebook Style */}
      <div className="space-y-6">
        {portfolio.map((project: any) => (
          <div key={project.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            {/* Project Header */}
            <div className="p-4 border-b border-gray-100 flex justify-between items-start">
              <div className="flex-1">
                <h3 className="font-bold text-lg text-gray-900">{project.title}</h3>
                <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                  {project.category && (
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                      {project.category}
                    </span>
                  )}
                  {project.completedDate && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(project.completedDate).toLocaleDateString('en-IE', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  )}
                  {project.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {project.location}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => removeProject(project.id)}
                className="text-gray-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"
                title="Delete project"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>

            {/* Project Images Grid */}
            {project.images && project.images.length > 0 && (
              <div className={`grid gap-1 ${
                project.images.length === 1 ? 'grid-cols-1' :
                project.images.length === 2 ? 'grid-cols-2' :
                project.images.length === 3 ? 'grid-cols-3' :
                project.images.length === 4 ? 'grid-cols-2' :
                'grid-cols-3'
              }`}>
                {project.images.slice(0, 6).map((img: ProjectImage, idx: number) => (
                  <div key={idx} className="relative aspect-square bg-gray-100">
                    <img
                      src={img.url}
                      alt={`${project.title} - ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {idx === 5 && project.images.length > 6 && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span className="text-white text-2xl font-bold">
                          +{project.images.length - 6}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Project Description */}
            <div className="p-4">
              <p className="text-gray-700 whitespace-pre-wrap">{project.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Add New Project Form */}
      {showForm ? (
        <div className="bg-white border-2 border-primary-200 rounded-xl shadow-lg p-6 space-y-5">
          <div className="flex items-center justify-between pb-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">Add New Project</h3>
            <button
              onClick={() => {
                setShowForm(false);
                setCurrentProject({ 
                  title: '', 
                  description: '', 
                  category: '', 
                  completedDate: '', 
                  location: '',
                  images: []
                });
                setDateError('');
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Project Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Project Title *
            </label>
            <input
              type="text"
              value={currentProject.title}
              onChange={(e) => setCurrentProject({ ...currentProject, title: e.target.value })}
              placeholder="e.g., Kitchen Renovation in Dublin"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Project Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              value={currentProject.description}
              onChange={(e) => setCurrentProject({ ...currentProject, description: e.target.value })}
              rows={4}
              placeholder="Describe the project, challenges, and results..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Project Images * (Max 10)
            </label>
            
            {/* Image Preview Grid */}
            {currentProject.images.length > 0 && (
              <div className="grid grid-cols-4 gap-3 mb-3">
                {currentProject.images.map((img, idx) => (
                  <div key={idx} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group">
                    <img
                      src={img.url}
                      alt={`Preview ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removeImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Button */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={currentProject.images.length >= 10}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ImageIcon className="w-5 h-5" />
              <span className="font-medium">
                {currentProject.images.length === 0 
                  ? 'Upload Images' 
                  : `Add More Images (${currentProject.images.length}/10)`
                }
              </span>
            </button>
          </div>

          {/* Additional Details */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category
              </label>
              <input
                type="text"
                value={currentProject.category}
                onChange={(e) => setCurrentProject({ ...currentProject, category: e.target.value })}
                placeholder="e.g., Plumbing"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Completion Date
              </label>
              <input
                type="date"
                value={currentProject.completedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                max={getTodayDate()}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  dateError ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {dateError && (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {dateError}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Location
              </label>
              <input
                type="text"
                value={currentProject.location}
                onChange={(e) => setCurrentProject({ ...currentProject, location: e.target.value })}
                placeholder="e.g., Dublin"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button 
              onClick={addProject}
              disabled={!currentProject.title || !currentProject.description || currentProject.images.length === 0 || !!dateError}
              className="flex-1"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add to Portfolio
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowForm(false);
                setCurrentProject({ 
                  title: '', 
                  description: '', 
                  category: '', 
                  completedDate: '', 
                  location: '',
                  images: []
                });
                setDateError('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-primary-600"
        >
          <Plus className="w-5 h-5" />
          <span className="font-semibold">Add New Project</span>
        </button>
      )}

      {portfolio.length === 0 && !showForm && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium mb-1">No projects yet</p>
          <p className="text-sm text-gray-500">Add your completed projects to showcase your work</p>
        </div>
      )}
    </div>
  );
}
